/**
 * Secure link page for a sheriff's office not on CivilHound: view/print the original paper and send the
 * return back. No account needed — the link is the key.
 */
import { useState } from 'react';
import { api, fileToBase64 } from '../shared/api.ts';
import { Card, ErrorBox, Field, Notice, Spinner, useAction, useLoad } from '../shared/ui.tsx';

const fmt = (d: string) => new Date(`${d.slice(0, 10)}T12:00:00Z`).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric', timeZone: 'UTC' });

export function TransferLink({ token }: { token: string }) {
  const r = useLoad(() => api.get<any>(`/api/public/transfers/${token}`), [token]);
  const a = useAction();
  const [f, setF] = useState({ outcome: 'served', date: '', servedTo: '', method: 'personal', officer: '', notes: '' });
  const [files, setFiles] = useState<File[]>([]);
  const set = (k: keyof typeof f) => (e: { target: { value: string } }) => setF({ ...f, [k]: e.target.value });
  const send = () =>
    a.run(async () => {
      const payload = await Promise.all(files.map(async (x) => ({ filename: x.name, dataBase64: await fileToBase64(x) })));
      await api.post(`/api/public/transfers/${token}/return`, { ...f, date: f.date || undefined, files: payload });
      r.reload();
    });
  return (
    <div className="xfer-page">
      <header className="xfer-top">
        <img src="/civilhound-icon.webp" alt="" width={36} height={36} />
        <div>
          <strong>CivilHound</strong>
          <div className="small">Civil paper sent for service</div>
        </div>
      </header>
      <main className="xfer-main stack">
        {r.error ? (
          <ErrorBox error={r.error} />
        ) : !r.data ? (
          <Spinner />
        ) : (
          <>
            <div>
              <h1>
                {r.data.from} sent {r.data.to} a paper to serve
              </h1>
              <p className="muted">
                Sent {new Date(r.data.sentAt).toLocaleDateString()} · questions: {r.data.from}
                {r.data.fromPhone ? `, ${r.data.fromPhone}` : ''}
              </p>
            </div>
            <Card title="Serve">
              <dl className="kv">
                <dt>Defendant</dt>
                <dd>
                  <strong>{r.data.snapshot.defendant}</strong>
                  {r.data.snapshot.defendantPhone ? ` · ${r.data.snapshot.defendantPhone}` : ''}
                </dd>
                <dt>Address</dt>
                <dd>{[r.data.snapshot.address.line1, r.data.snapshot.address.line2, r.data.snapshot.address.city, [r.data.snapshot.address.state, r.data.snapshot.address.zip].filter(Boolean).join(' ')].filter(Boolean).join(', ')}</dd>
                <dt>Paper</dt>
                <dd>
                  {r.data.snapshot.paperTypeName} · case <span className="mono">{r.data.snapshot.caseNumber}</span>
                </dd>
                <dt>Plaintiff</dt>
                <dd>{r.data.snapshot.plaintiff ?? '—'}</dd>
                <dt>Court date</dt>
                <dd>{r.data.snapshot.courtDate ? `${fmt(r.data.snapshot.courtDate)}${r.data.snapshot.courtTime ? `, ${r.data.snapshot.courtTime}` : ''}` : '—'}</dd>
                {r.data.note && (
                  <>
                    <dt>Note</dt>
                    <dd>{r.data.note}</dd>
                  </>
                )}
              </dl>
              <div className="row gap wrap">
                {r.data.files
                  .filter((x: any) => x.direction === 'outbound')
                  .map((x: any) => (
                    <a key={x.id} className="btn primary" href={`/api/public/transfers/${token}/files/${x.id}`} target="_blank" rel="noreferrer">
                      📄 Open / print the original paper
                    </a>
                  ))}
              </div>
            </Card>
            {['returned', 'completed'].includes(r.data.status) ? (
              <Notice tone="ok">
                ✓ Return received by {r.data.from}: {r.data.returnOutcome === 'served' ? 'served' : 'not served'}
                {r.data.returnDetails?.date ? ` ${fmt(r.data.returnDetails.date)}` : ''}. Thank you.
              </Notice>
            ) : (
              <Card title={`Send the return to ${r.data.from}`}>
                <div className="grid2">
                  <Field label="Result">
                    <select value={f.outcome} onChange={set('outcome')}>
                      <option value="served">Served</option>
                      <option value="non_served">Not served</option>
                    </select>
                  </Field>
                  <Field label="Date">
                    <input type="date" value={f.date} onChange={set('date')} />
                  </Field>
                  {f.outcome === 'served' && (
                    <>
                      <Field label="Served to">
                        <input value={f.servedTo} onChange={set('servedTo')} />
                      </Field>
                      <Field label="How">
                        <select value={f.method} onChange={set('method')}>
                          <option value="personal">Personal</option>
                          <option value="substitute">Substitute</option>
                          <option value="posting">Posting</option>
                        </select>
                      </Field>
                    </>
                  )}
                  <Field label="Officer name *">
                    <input value={f.officer} onChange={set('officer')} />
                  </Field>
                </div>
                <Field label="Notes">
                  <textarea rows={2} value={f.notes} onChange={set('notes')} />
                </Field>
                <Field label="Signed return / affidavit (PDF) *">
                  <input type="file" accept="application/pdf" multiple aria-label="Return PDF" onChange={(e) => setFiles([...(e.target.files ?? [])])} />
                </Field>
                <ErrorBox error={a.error} />
                <button className="btn primary big" disabled={a.busy || !files.length || f.officer.trim().length < 2} onClick={send}>
                  {a.busy ? 'Sending…' : 'Send return'}
                </button>
              </Card>
            )}
          </>
        )}
      </main>
    </div>
  );
}
